/**
 * Locale utilities for @entrata/i18n integration.
 *
 * Precedence chain (highest → lowest):
 *   1. ?locale= query param  — seeded into Redux by AppWrapper on mount
 *   2. Redux state           — set by LanguageSwitcher or property default
 *   3. navigator.language    — mapped to nearest SUPPORTED_LOCALE
 *   4. 'en-US'               — hard fallback
 *
 * Validation happens at the point of entry (e.g. AppWrapper URL seeding) so
 * nothing unsupported should reach @entrata/i18n.
 */

/**
 * Locales supported by @entrata/i18n for date, currency, and number formatting.
 */
export const SUPPORTED_LOCALES = [
  'en-CA',
  'en-GB',
  'en-IE',
  'en-US',
  'es-ES',
  'es-MX',
  'es-CL',
  'es-US',
  'fr-FR',
  'fr-CA',
  'hi-IN',
  'zh-CN',
  'pt-BR',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Maps a browser locale (e.g. from navigator.language) to a supported locale.
 * If the locale is supported, returns it. Otherwise maps to a regional variant
 * or falls back to en-US.
 */
export function getSupportedLocale(browserLocale: string): string {
  if (!browserLocale || typeof browserLocale !== 'string') {
    return 'en-US';
  }
  const normalized = browserLocale.trim().replace(/_/g, '-');
  if (!normalized) return 'en-US';
  if (SUPPORTED_LOCALES.includes(normalized as SupportedLocale)) {
    return normalized;
  }
  const lang = normalized.split('-')[0]?.toLowerCase();
  if (!lang) return 'en-US';
  const regionalMatch = SUPPORTED_LOCALES.find((l) => l.toLowerCase().startsWith(lang));
  return regionalMatch ?? 'en-US';
}

/**
 * Returns the browser's locale mapped to a supported locale.
 * Use as fallback when no explicit locale is set via URL or language dropdown.
 */
export function getBrowserLocale(): string {
  if (typeof navigator === 'undefined' || !navigator.language) {
    return 'en-US';
  }
  return getSupportedLocale(navigator.language);
}

/**
 * Validates an arbitrary locale code and returns the nearest supported locale.
 * Use this at every point where a locale value enters the system (URL params,
 * API responses, user selection) so unsupported values never reach formatting
 * or translation layers.
 */
export function validateLocale(code: string): string {
  return getSupportedLocale(code);
}
